import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { EasingLogic, PageScrollService } from 'ngx-page-scroll-core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { HorizontalScrollItemDirective } from './horizontal-scroll-item.directive';
import { HorizontalScrollTranslations } from './horizontal-scroll-translations.model';

@Component({
  selector: 'zelis-horizontal-scroll',
  templateUrl: './horizontal-scroll.component.html',
  styleUrls: ['./horizontal-scroll.component.scss'],
})
export class HorizontalScrollComponent
  implements AfterContentInit, AfterViewInit, OnDestroy
{
  @HostBinding() class = 'zelis-dls';

  @Input() public centerFixedContent = false;
  @Input() public disableScrollWrap = false;
  @Input() public disableProgressDots = false;
  @Input() public scrollByGroup = true;
  @Input() public scrollDuration = 800;
  @Input() public scrollOffset = 48;
  @Input() public scrollDistance = 400;
  @Input() public set translations(value: HorizontalScrollTranslations) {
    this._translations = new HorizontalScrollTranslations(value);
  }
  public get translations(): HorizontalScrollTranslations {
    return this._translations;
  }
  @Output() public activeIndexChange: EventEmitter<number | null> =
    new EventEmitter();

  @ViewChild('ScrollStage') private scrollStage!: ElementRef;
  @ViewChild('ScrollItemContainer') private scrollItemContainer!: ElementRef;

  @ContentChildren(forwardRef(() => HorizontalScrollItemDirective), {
    read: ElementRef,
    descendants: true,
  })
  public items: QueryList<ElementRef> = new QueryList<ElementRef>();
  public showControls = false;
  public showProgressDots = false;
  public isScrollBeginning = true;
  public isScrollEnd = false;
  public activeScrollIndex = 0;
  public scrollIndexes: number[] = [];
  public previousIndex: number | null = null;
  public isStageScrollable: BehaviorSubject<boolean> = new BehaviorSubject(
    true
  );

  private itemChangeSubscription: Subscription = new Subscription();
  private _translations: HorizontalScrollTranslations =
    new HorizontalScrollTranslations();

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.initScrollStage();
  }

  constructor(
    private pageScrollService: PageScrollService,
    private breakpointObserver: BreakpointObserver,
    @Inject(DOCUMENT) private document: any
  ) {}

  ngAfterContentInit() {
    this.subscribeToItemChanges();
    this.resetActiveIndex();
  }

  ngAfterViewInit() {
    this.initScrollStage();
  }

  ngOnDestroy() {
    this.itemChangeSubscription.unsubscribe();
  }

  private initScrollStage() {
    const isSmallScreen =
      this.breakpointObserver.isMatched('(max-width: 639px)');

    this.setStageIsScrollable();
    this.showControls = !isSmallScreen && this.stageIsScrollable();
    this.showProgressDots =
      !isSmallScreen && !this.disableProgressDots && this.stageIsScrollable();
    this.updateScrollButtons();
  }

  public setContentChildren(children: QueryList<ElementRef>) {
    this.items = children;
    this.initScrollStage();
  }

  public nextItem(): void {
    if (this.scrollByGroup) {
      this.scrollNextGroup();
    } else {
      this.scrollStage.nativeElement.scrollLeft += this.scrollDistance;
    }
  }

  public previousItem(): void {
    if (this.scrollByGroup) {
      this.scrollPreviousGroup();
    } else {
      this.scrollStage.nativeElement.scrollLeft -= this.scrollDistance;
    }
  }

  public updateScrollButtons() {
    setTimeout(() => {
      const containerWidth = this.scrollStage?.nativeElement?.offsetWidth;
      const scrollLeft = this.scrollStage?.nativeElement?.scrollLeft;
      const scrollWidth = this.scrollStage?.nativeElement?.scrollWidth;

      this.isScrollBeginning = scrollLeft === 0;
      this.isScrollEnd = scrollWidth - (scrollLeft + containerWidth) <= 8; // 8px buffer for small changes in scroll position caused by menus
    });
  }

  private scrollNextGroup() {
    const nextIndex = this.scrollIndexes[this.activeScrollIndex + 1];

    if (this.indexIsValid(nextIndex)) {
      this.goToIndex(nextIndex);
      this.activeScrollIndex++;
      this.isScrollEnd =
        this.activeScrollIndex === this.scrollIndexes.length - 1;
    } else {
      this.goToIndex(0);
      this.activeScrollIndex = 0;
    }
  }

  private scrollPreviousGroup() {
    const prevIndex = this.scrollIndexes[this.activeScrollIndex - 1];

    if (this.indexIsValid(prevIndex)) {
      this.goToIndex(prevIndex);
      this.activeScrollIndex--;
    } else {
      this.goToIndex(0);
      this.activeScrollIndex = 0;
    }
  }

  private goToIndex(index: number): void {
    this.scrollTo(index);
    this.activeIndexChange.emit(index);
  }

  private setStageIsScrollable(): void {
    this.isStageScrollable.next(this.stageIsScrollable());
  }

  private stageIsScrollable(): boolean {
    const stageElement = this.scrollStage?.nativeElement;
    const stageMarginOffset = 50;
    // First child node is the scrollable content.
    const itemContainerElement =
      this.scrollItemContainer?.nativeElement.childNodes[0];

    if (!stageElement || !itemContainerElement) {
      return false;
    }

    this.calcItemsPerGroup(stageElement, itemContainerElement);

    return (
      itemContainerElement.offsetWidth >
      stageElement.offsetWidth - stageMarginOffset
    );
  }

  private calcItemsPerGroup(
    stageElement: HTMLDivElement,
    itemContainerElement: HTMLDivElement
  ) {
    const scrollItems = itemContainerElement?.childNodes;
    const itemGapOffset = 8;
    const controlPositionOffset = 68; // offset width to compensate for arrow control button overlap
    let itemCount = 0;
    let groupWidth = 8; // offset for scroll stage's padding
    this.scrollIndexes = [0];

    scrollItems.forEach((item: any) => {
      if (
        groupWidth + item.offsetWidth >
        stageElement.offsetWidth - controlPositionOffset
      ) {
        this.scrollIndexes.push(itemCount);
        groupWidth = this.scrollOffset; // offset to compensate for partially visible previous group's last card
      }

      groupWidth += item.offsetWidth + itemGapOffset;
      itemCount++;
    });
  }

  private scrollTo(index: number): void {
    this.pageScrollService.scroll({
      document: this.document,
      scrollTarget: this.items.get(index)?.nativeElement,
      scrollViews: [this.scrollStage?.nativeElement],
      verticalScrolling: false,
      duration: this.scrollDuration,
      scrollOffset: this.scrollOffset,
      interruptible: false,
      easingLogic: this.easeInOutExpo,
    });
  }

  private resetActiveIndex(): void {
    this.activeScrollIndex = 0;
    this.activeIndexChange.emit(0);
  }

  private subscribeToItemChanges(): void {
    this.itemChangeSubscription = this.items.changes.subscribe(() => {
      this.initScrollStage();
      this.resetActiveIndex();
    });
  }

  private indexIsValid(index: number): boolean {
    return index <= this.items.length - 1 && index >= 0;
  }

  private easeInOutExpo: EasingLogic = (
    t: number,
    b: number,
    c: number,
    d: number
  ): number => {
    if (t === 0) {
      return b;
    }
    if (t === d) {
      return b + c;
    }
    if ((t /= d / 2) < 1) {
      return (c / 2) * Math.pow(2, 10 * (t - 1)) + b;
    }
    return (c / 2) * (-Math.pow(2, -10 * --t) + 2) + b;
  };
}
